import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { RenderElementProps } from 'slate-react';
import { VIDEO_TYPE } from '../../../interface/common/Video';
import styles from './VideoElement.module.scss';
import classnames from 'classnames/bind';
import { HasOption } from '../../../interface/common/hasOption';
import { useSelected, useSlate } from 'slate-react';
import { Editor, Element as SlateElement, Transforms } from 'slate';
import { STYLE_WIDTH, VIDEO_ALIGN } from './VideoElement.constant';
import RadioGroup from '../../radiogroup/RadioGroup';
import Icon, { IconTypes } from '../../icon/Icon';
import { COLORS } from '../../../constants/colors';
import Paragraph from '../../paragraph/Paragraph';
import TextFieldBase from '../../textfield/TextFieldBase';
import Portal from '../../portal/Portal';
import IconButton from '../../button/IconButton';
import { isNumberReg } from '../../../util/reg';
import { EditorAlign } from '../textEditor.type';

const cx = classnames.bind(styles);

const isActive = (editor: Editor) => {
  if (editor.selection) {
    const [match] = Editor.nodes(editor, {
      at: Editor.unhangRange(editor, editor.selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'video',
    });

    return match;
  }
  return;
};

const TOOLBAR_WIDTH_OFFSET = 760;
const TOOLBAR_HEIGHT_OFFSET = 50;
const TOOLBAR_LEFT_OFFSET = 300;

const VideoElement = ({
  attributes,
  children,
  element,
  readOnly,
}: RenderElementProps & { isPreview?: boolean; readOnly?: boolean } & HasOption) => {
  const { videoType, videoUrl } = element;
  const selected = useSelected();
  const editor = useSlate();

  const videoBlock = isActive(editor);

  const [videoSize, setVideoSize] = useState<'S' | 'M' | 'L' | 'FULL' | 'CUSTOM'>('CUSTOM');
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
  const [isPlayable, setIsPlayable] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!videoRef.current) return;

    const { left, top } = videoRef.current.getBoundingClientRect();
    setTooltipPosition({
      left: window.innerWidth < left + TOOLBAR_WIDTH_OFFSET ? left - TOOLBAR_LEFT_OFFSET : left,
      top: top + window.scrollY - TOOLBAR_HEIGHT_OFFSET,
    });
  }, [videoRef, !!videoBlock, window.scrollY]);

  useEffect(() => {
    if (document.querySelector('[data-id="slate-editor"]')) {
      document.querySelector('[data-id="slate-editor"]')?.addEventListener('scroll', handleDeselect);
    }
    return () => {
      document.querySelector('[data-id="slate-editor"]')?.removeEventListener('scroll', handleDeselect);
    };
  }, []);

  useEffect(() => {
    if (selected && videoBlock) {
      setIsPlayable(true);
    } else {
      setIsPlayable(false);
    }
  }, [selected, videoBlock]);

  const handleDeselect = () => {
    if (isActive(editor)) {
      Transforms.deselect(editor);
    }
  };

  const setVideoBlockStyle = (props: { width?: number; borderRadius?: number; align?: EditorAlign }) => {
    Transforms.setNodes(editor, {
      ...element,
      videoStyle: {
        ...element.videoStyle!,
        ...props,
      },
    });
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>, style: 'width' | 'borderRadius') => {
    if (!isNumberReg(`${e.target.value}`)) {
      setVideoBlockStyle({
        [style]: 0,
      });
      return;
    }

    const maxValue = {
      width: 760,
      borderRadius: 999,
    };

    const value = Number.isNaN(Number(e.target.value))
      ? 0
      : Number(e.target.value) > maxValue[style]
      ? maxValue[style]
      : Number(e.target.value);

    setVideoBlockStyle({
      [style]: value,
    });
  };

  const handleOnChangeAlign = (align: EditorAlign) => {
    setVideoBlockStyle({
      align,
    });
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    if (readOnly) return;

    e.preventDefault();
    e.stopPropagation();

    if (!selected) {
      const path = editor.selection ? editor.selection.anchor.path : [0];
      const range = Editor.range(editor, path);
      Transforms.select(editor, range);
    }
  };

  const renderVideo = () => {
    if (videoType === VIDEO_TYPE.YOUTUBE && videoUrl) {
      return (
        <div className={cx('video-content')}>
          <ReactPlayer
            url={videoUrl}
            width={'100%'}
            height={'100%'}
            controls={isPlayable}
            config={{
              youtube: {
                playerVars: {
                  rel: 0,
                },
              },
            }}
          />
          {!isPlayable && !readOnly && <div className={cx('video-overlay')} onClick={handleVideoClick} />}
        </div>
      );
    }

    if (videoType === VIDEO_TYPE.FILE && videoUrl) {
      return (
        <div className={cx('video-content')}>
          <video controls={isPlayable} width={'100%'} height={'100%'} style={{ maxWidth: '100%' }}>
            <source src={videoUrl} type={'video/mp4'} />
            Your browser does not support the video tag.
          </video>
          {!isPlayable && !readOnly && <div className={cx('video-overlay')} onClick={handleVideoClick} />}
        </div>
      );
    }

    return null;
  };

  return (
    <div {...attributes} className={cx('container')}>
      {children}
      {!readOnly && selected && !!videoBlock && (
        <Portal domId={'modal-root'}>
          <div
            className={cx('video-util')}
            ref={tooltipRef}
            style={{
              left: `${tooltipPosition.left}px`,
              top: `${tooltipPosition.top}px`,
            }}
          >
            <div className={cx('radio-group')}>
              <RadioGroup
                id={'slate-editor-video'}
                name={'slate-editor-video'}
                onChange={(e) => {
                  const value = e.target.value as 'S' | 'M' | 'L' | 'FULL' | 'CUSTOM';
                  setVideoSize(value);
                  if (value === 'CUSTOM') {
                    setVideoBlockStyle({
                      width: videoRef.current?.offsetWidth,
                    });
                    return;
                  }
                  setVideoBlockStyle({
                    width: STYLE_WIDTH[value],
                  });
                }}
                value={videoSize}
              >
                {[
                  { name: 'S', value: 'S' },
                  { name: 'M', value: 'M' },
                  { name: 'L', value: 'L' },
                  { name: 'Full', value: 'FULL' },
                  { name: <Icon name={'more_horiz_line'} color={COLORS.gray800} size={24} />, value: 'CUSTOM' },
                ].map(({ name, value }) => (
                  <RadioGroup.Option key={name + value} value={value}>
                    <RadioGroup.Consumer>
                      {({ selectValue }) => (
                        <div className={cx('radio', { active: selectValue === value })}>{name}</div>
                      )}
                    </RadioGroup.Consumer>
                  </RadioGroup.Option>
                ))}
              </RadioGroup>
            </div>
            <div className={cx('divider')} />
            {videoSize === 'CUSTOM' && (
              <>
                <div className={cx('custom-size')}>
                  {(() => {
                    if (!videoRef.current) return;

                    const getStyle = {
                      width: videoRef.current?.offsetWidth,
                    };

                    const videoWidth = element?.videoStyle!['width'] ?? getStyle['width'];

                    return (
                      <div className={cx('style-wrapper', { disabled: videoSize !== 'CUSTOM' })}>
                        <Paragraph variant={'B3'}>가로 크기</Paragraph>
                        <TextFieldBase
                          className={cx('style-text')}
                          size={'sm'}
                          maxLength={3}
                          value={videoWidth}
                          onChange={(e) => handleOnChange(e, 'width')}
                          disabled={videoSize !== 'CUSTOM'}
                        />
                      </div>
                    );
                  })()}
                </div>
                <div className={cx('divider')} />
              </>
            )}
            <div className={cx('custom-size')}>
              {(
                [
                  { icon: 'hori_align_left', value: 'left' },
                  { icon: 'hori_align_center', value: 'center' },
                  { icon: 'hori_align_right', value: 'right' },
                ] as Array<{ icon: IconTypes; value: EditorAlign }>
              ).map(({ icon, value }) => (
                <IconButton
                  key={value}
                  className={cx('align_icon_button', { active: value === element?.videoStyle?.align ?? 'center' })}
                  type={'button'}
                  icon={icon}
                  size={24}
                  color={COLORS.gray800}
                  onClick={() => {
                    handleOnChangeAlign(value);
                  }}
                />
              ))}
            </div>
            <div className={cx('divider')} />
            <div className={cx('custom-size')}>
              <Icon name={'corner_radius_24'} size={24} color={COLORS.gray800} />
              <TextFieldBase
                className={cx('border-text')}
                size={'sm'}
                maxLength={3}
                value={element?.videoStyle?.borderRadius ?? 0}
                onChange={(e) => handleOnChange(e, 'borderRadius')}
              />
            </div>
          </div>
        </Portal>
      )}
      <div
        className={cx('video-container')}
        style={{
          justifyContent: VIDEO_ALIGN[element.videoStyle?.align as EditorAlign] ?? 'center',
        }}
      >
        <div
          className={cx('video-wrapper', { active: !readOnly && selected })}
          contentEditable={false}
          ref={videoRef}
          style={{
            width: element.videoStyle?.width ? `${Math.min(760, element.videoStyle.width)}px` : '100%',
            borderRadius: `${element.videoStyle?.borderRadius ?? 0}px`,
            overflow: 'hidden',
          }}
        >
          {renderVideo()}
        </div>
      </div>
    </div>
  );
};

export default VideoElement;
